import { useEffect, useState } from 'react';
import api from '../services/api';
import { useV3Prefs } from './V3PrefsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The picture is fetched once per visit and kept in this browser tab's session storage, keyed by the photo's id (a new photo has a new id).
export const avatarCacheKey = (photoId) => `c30_v3_avatar_${photoId}`;

// Up to two initials from a name or an email address ("Jamie Moseley" gives JM, "j.moseley@encon.co.uk" gives J).
export const initialsFor = (name) => {
  const words = String(name || '').split('@')[0].trim().split(/[\s._-]+/).filter(Boolean);
  const letters = words.slice(0, 2).map((word) => word[0].toUpperCase()).join('');
  return letters || '?';
};

export const useAvatarSrc = (photoId) => {
  const [src, setSrc] = useState(() => {
    if (!photoId) return null;
    try { return sessionStorage.getItem(avatarCacheKey(photoId)); } catch (_) { return null; }
  });
  useEffect(() => {
    if (!photoId) { setSrc(null); return undefined; }
    try {
      const cached = sessionStorage.getItem(avatarCacheKey(photoId));
      if (cached) { setSrc(cached); return undefined; }
    } catch (_) { /* private mode */ }
    let cancelled = false;
    api.get(`${API}/photos/${photoId}`, { params: { format: 'base64' } })
      .then((response) => {
        if (cancelled) return;
        const data = response.data && response.data.photo;
        if (!data) { setSrc(null); return; }
        setSrc(data);
        try { sessionStorage.setItem(avatarCacheKey(photoId), data); } catch (_) { /* too large or private mode */ }
      })
      .catch(() => { if (!cancelled) setSrc(null); }); // an old photo that has been cleaned away: the initials show instead
    return () => { cancelled = true; };
  }, [photoId]);
  return src;
};

// The person's round picture: their photo, or their initials when they have not added one.
const V3Avatar = ({ user, size = 40, className = '' }) => {
  const { profile } = useV3Prefs();
  const src = useAvatarSrc(profile.photoId);
  const label = profile.nickname || user?.full_name || user?.username || user?.email || '';
  return (
    <span className={`v3-avatar-disc ${className}`} style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}>
      {src ? <img src={src} alt="" /> : <span aria-hidden="true">{initialsFor(label)}</span>}
    </span>
  );
};

export default V3Avatar;
